import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { fetchAllDataAPI } from '../utils/api';

ChartJS.register(...registerables);

const LineChartOneYear = () => {
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [totalOrders, setTotalOrders] = useState(0);

    useEffect(() => {
        getLastOneYearChartData();
    }, []);

    // ✅ palette (fallback)
    const palette = useMemo(
        () => [
            '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
            '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab'
        ],
        []
    );

    const groupPalette = useMemo(
        () => ({
            Delivered: '#59a14f',
            'In Transit': '#4e79a7',
            RTO: '#f28e2b',
            Cancelled: '#e15759',
            Returned: '#af7aa1',
            'Failed Delivery': '#9c755f',
            'Lost / Damaged': '#333333',
            'Hold / Exception': '#edc949',
            'Pending / New': '#76b7b2',
            Other: '#bab0ab',
            'All Orders': '#bab0ab',
        }),
        []
    );

    const getColor = (i) => palette[i % palette.length];

    const cleanNumberArray = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.map((v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        });
    };

    const cleanColor = (val, fallback) => {
        return typeof val === 'string' && val ? val : fallback;
    };

    const normalizeStatus = (label = '') => String(label || '').trim().toLowerCase();

    // ✅ Map raw courier statuses to short groups
    const getShortGroup = (rawLabel) => {
        const s = normalizeStatus(rawLabel);
        if (!s) return 'Other';

        // Delivered
        if (
            s === 'delivered' ||
            s.includes('rto_delivered') ||
            s.includes('rto delivered') ||
            s.includes('delivered back to shipper') ||
            s.includes('reached back at seller city')
        ) return 'Delivered';

        // In Transit
        if (
            s.includes('in transit') ||
            s.includes('out for delivery') ||
            s.includes('ot:rto in transit') ||
            s.includes('shipment inscan') ||
            s.includes('await delivery information') ||
            s.includes('out_for_delivery')
        ) return 'In Transit';

        // RTO
        if (
            (s.includes('rto') && !s.includes('rto_delivered') && !s.includes('rto delivered')) ||
            s.includes('rto initiated') ||
            s.includes('in rto/rts process') ||
            s.includes('returned to origin initiated') ||
            s.includes("returned to origin at shipper's request") ||
            s.includes('dispatched - dispatched for rto')
        ) return 'RTO';

        // Cancelled
        if (s.includes('cancelled') || s.includes('canceled') || s.includes('shipment cancelled'))
            return 'Cancelled';

        // Returned
        if (s === 'returned' || s.includes('returned back') || s.includes('returned to client'))
            return 'Returned';

        // Failed Delivery
        if (
            s.includes('failed attempt') ||
            s.includes('delivery attempted') ||
            s.includes('premises closed') ||
            s.includes('pickup failed') ||
            s.includes('consignee refused to accept') ||
            s.includes('consignee not available')
        ) return 'Failed Delivery';

        // Lost / Damaged
        if (s.includes('lost') || s.includes('destroyed') || s.includes('prohibited'))
            return 'Lost / Damaged';

        // Hold / Exception
        if (
            s.includes('hold') ||
            s.includes('exception') ||
            s.includes('incorrect waybill') ||
            s.includes('address incorrect') ||
            s.includes('consignee address incorrect') ||
            s.includes('necessary charges') ||
            s.includes('delivery delayed') ||
            s.includes('shipment untraceable')
        ) return 'Hold / Exception';

        // Pending / New
        if (
            s.includes('pending') ||
            s.includes('new') ||
            s.includes('placed') ||
            s.includes('confirmed') ||
            s.includes('shipment booked') ||
            s.includes('contact customer service')
        ) return 'Pending / New';

        return 'Other';
    };

    // ✅ Line dataset sanitizer (fix null colors causing "reading 'a'")
    const normalizeLineDatasets = (datasets = [], labelsLen = 0) => {
        return datasets.map((ds, i) => {
            const base = getColor(i);

            let data = cleanNumberArray(ds?.data);
            if (labelsLen && data.length < labelsLen) {
                data = [...data, ...new Array(labelsLen - data.length).fill(0)];
            }

            const borderColor = cleanColor(ds?.borderColor, base);
            const backgroundColor = cleanColor(ds?.backgroundColor, borderColor);

            return {
                ...ds,
                data,
                borderColor,
                backgroundColor,
                pointBackgroundColor: cleanColor(ds?.pointBackgroundColor, borderColor),
                pointBorderColor: cleanColor(ds?.pointBorderColor, '#ffffff'),
                pointHoverBackgroundColor: cleanColor(ds?.pointHoverBackgroundColor, borderColor),
                pointHoverBorderColor: cleanColor(ds?.pointHoverBorderColor, '#ffffff'),
                borderWidth: ds?.borderWidth ?? 2,
                tension: ds?.tension ?? 0.35,
                fill: ds?.fill ?? false,
            };
        });
    };

    // ✅ Group many statuses into short groups for Line chart
    const groupLineDatasets = (labels, datasets) => {
        const groupsOrder = [
            'Delivered',
            'In Transit',
            'RTO',
            'Cancelled',
            'Returned',
            'Failed Delivery',
            'Lost / Damaged',
            'Hold / Exception',
            'Pending / New',
            'Other',
        ];

        const groupedMap = {};
        groupsOrder.forEach((g) => (groupedMap[g] = new Array(labels.length).fill(0)));

        datasets.forEach((ds) => {
            const label = ds?.label || '';
            if (normalizeStatus(label) === 'all orders') return;

            const group = getShortGroup(label);
            const arr = Array.isArray(ds?.data) ? ds.data : [];

            for (let i = 0; i < labels.length; i++) {
                groupedMap[group][i] += Number(arr[i] || 0);
            }
        });

        const groupedDatasets = groupsOrder
            .filter((g) => groupedMap[g].some((v) => Number(v) > 0))
            .map((g, idx) => {
                const color = groupPalette[g] || getColor(idx);
                return {
                    label: g,
                    data: groupedMap[g],
                    borderColor: color,
                    backgroundColor: color,
                    borderWidth: 2,
                    tension: 0.35,
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                };
            });

        return groupedDatasets;
    };

    const getLastOneYearChartData = async () => {
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI('super-admin-get-last-one-year-data-for-chart');

            if (res?.data?.status === 1) {
                const labels = Array.isArray(res?.data?.chartdata?.labels) ? res.data.chartdata.labels : [];
                const datasetsRaw = Array.isArray(res?.data?.chartdata?.datasets) ? res.data.chartdata.datasets : [];

                // 1) sanitize incoming (null colors fix)
                const datasetsSanitized = normalizeLineDatasets(datasetsRaw, labels.length);

                // 2) group
                const datasetsGrouped = groupLineDatasets(labels, datasetsSanitized);

                // total
                let total = 0;
                datasetsGrouped.forEach((dataset) => {
                    total += (dataset.data || []).reduce((sum, v) => sum + (Number(v) || 0), 0);
                });
                setTotalOrders(total);

                setChartData({ labels, datasets: datasetsGrouped });
            } else {
                console.error('Error fetching chart data:', res?.data?.msg);
                setChartData(null);
            }
        } catch (error) {
            console.error('Error fetching chart data:', error);
            setChartData(null);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white rounded shadow">
            {isLoading ? (
                <p>Loading...</p>
            ) : chartData ? (
                <Line
                    data={chartData}
                    options={{
                        responsive: true,
                        plugins: {
                            legend: { position: 'top' },
                            title: {
                                display: true,
                                text: `Last 1 Year Orders: ${totalOrders}`,
                                font: { size: 18, weight: 'bold' },
                                color: '#333333',
                            },
                            tooltip: {
                                callbacks: {
                                    label: (context) => `${context.dataset.label}: ${context.raw}`,
                                },
                            },
                        },
                        scales: {
                            x: {
                                title: {
                                    display: true,
                                    text: 'Months',
                                    font: { size: 16, weight: 'bold' },
                                    color: '#ff5733',
                                },
                                grid: { display: false },
                            },
                            y: {
                                beginAtZero: true,
                                title: {
                                    display: true,
                                    text: 'Order Count',
                                    font: { size: 16, weight: 'bold' },
                                    color: '#333333',
                                },
                            },
                        },
                        elements: {
                            point: { radius: 2, hoverRadius: 5 },
                            line: { borderWidth: 2 },
                        },
                    }}
                />
            ) : (
                <p>No data available.</p>
            )}
        </div>
    );
};

export default LineChartOneYear;
